import {
  useFilterStore,
  useMapStore,
  usePointStore,
  usePolygonStore,
} from '@monopolio-ui/state-library'
import { EHeatMapCategory, ESearchLevel, IPolygonProperties } from '@monopolio-ui/types-library'
import { useEffect, useMemo, useRef } from 'react'
import {
  animateChangeHeatMapByCategories,
  animateHoverPolygon,
  filterPolygonByUrlCode,
  heatMapCaseExpressionColors,
  heatMapColors,
  tileZoomConfiguration,
} from '../../utils/heatMapUtils'
import PolygonPopup from './polygonPopup'
import { FilterSpecification } from 'mapbox-gl'
import { usePrevious } from '@wiggot/hooks/src/usePrevious'

const BlockPolygonManager = (): JSX.Element => {
  const { map, mapZoom, isFlyTo, drawingTool, isLoadedMap, hoveredLayer } =
    useMapStore()

  const { searchLevel } = useFilterStore()

  const {
    polygonTiles,
    heatMapCategory,
    isEnabledHeatMap,
    polygonNavigation,
    setPolygonNavigation,
  } = usePolygonStore()

  const { pointsSvgContainer } = usePointStore()

  const heatMapCategoryPrevious = usePrevious<EHeatMapCategory | null>(
    heatMapCategory
  )

  const mapZoomPrevious = usePrevious<number>(mapZoom)

  const pointsSvgContainerRef = useRef<any>(pointsSvgContainer)
  pointsSvgContainerRef.current = pointsSvgContainer

  const drawingToolRef = useRef<typeof drawingTool>(drawingTool)
  drawingToolRef.current = drawingTool

  const hoveredLayerRef = useRef<typeof hoveredLayer>(hoveredLayer)
  hoveredLayerRef.current = hoveredLayer

  const heatMapCategoryRef = useRef<EHeatMapCategory | null>(heatMapCategory)
  heatMapCategoryRef.current = heatMapCategory

  const blocksTiles = useMemo(() => {
    const filter = polygonTiles.filter((tile) => tile?.tiles?.blocks?.id)
    return filter.map((tile) => tile.tiles.blocks)
  }, [polygonTiles])

  const loadBlockLevel = (params: { category: EHeatMapCategory }): void => {
    if (!map) {
      return
    }

    const { category } = params

    for (const {
      tiles: {
        blocks: { id: blockId },
        properties: { id: propertyId },
      },
    } of polygonTiles) {
      if (!blockId) {
        continue
      }

      const layerName = `${blockId}-block-polygon`
      const source = `${blockId}-block-source`
      const sourceLayer = blockId

      const find = map?.getSource(source)

      if (find) {
        continue
      }

      const { block, property } = tileZoomConfiguration

      const minZoom = block.minZoom
      const maxZoom = propertyId ? block.maxZoom : property.maxZoom

      map?.addSource(source, {
        type: 'vector',
        url: `mapbox://davpach.${blockId}`,
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      map?.addLayer({
        id: layerName,
        type: 'fill',
        source,
        'source-layer': sourceLayer,
        filter:
          searchLevel === ESearchLevel.NEIGHBORHOOD
            ? ['==', 'id', 0]
            : ['!=', 'id', 0],
        paint: {
          'fill-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            ['feature-state', 'hover_color'],
            heatMapCaseExpressionColors[category],
          ],
          'fill-opacity': 0.4,
        },
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      map?.addLayer({
        id: `${blockId}-block-border`,
        type: 'line',
        source,
        'source-layer': sourceLayer,
        filter:
          searchLevel === ESearchLevel.NEIGHBORHOOD
            ? ['==', 'id', 0]
            : ['!=', 'id', 0],
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            heatMapColors[category].hover,
            '#0368FE',
          ],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            ['feature-state', 'hover_width'],
            1.678,
          ],
        },
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      createHoverEvent({ layerName, source, sourceLayer })
      createClickEvent({ layerName })
    }
  }

  const createHoverEvent = (params: {
    layerName: string
    source: string
    sourceLayer: string
  }) => {
    const { layerName, source, sourceLayer } = params

    let featureId: number | undefined

    map?.on('mousemove', layerName, ({ features }) => {
      if (!features?.length) {
        return
      }

      const feature = features[0]

      if (!feature) {
        return
      }

      if (featureId !== undefined && feature.id !== featureId) {
        if (
          !featureId ||
          (drawingToolRef.current.isActive &&
            drawingToolRef.current.status !== 'processed') ||
          hoveredLayerRef?.current?.layerId
        ) {
          return
        }

        map?.setFeatureState(
          {
            source,
            sourceLayer,
            id: featureId,
          },
          { hover: false }
        )
        featureId = undefined
        return
      }

      const { id } = feature

      if (featureId && featureId === id) {
        return
      }

      featureId = id as number

      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed') ||
        hoveredLayerRef?.current?.layerId
      ) {
        return
      }

      setTimeout(() => {
        pointsSvgContainerRef?.current?.style('cursor', 'pointer')
        map.getCanvas().style.cursor = 'pointer'
      }, 50)

      let indexKey = 'price_index_rank'
      if (heatMapCategoryRef?.current === EHeatMapCategory.RENT) {
        indexKey = 'rent_index_rank'
      } else if (heatMapCategoryRef?.current === EHeatMapCategory.CAPRATE) {
        indexKey = 'caprate_index_rank'
      }

      const codeLevel = feature.properties?.[indexKey]
      const heatMapColor = (heatMapCategoryRef?.current ?? heatMapCategory)as keyof (typeof heatMapColors)

      const bgColor =
        heatMapColors[heatMapColor][
          codeLevel as keyof (typeof heatMapColors)[EHeatMapCategory]
        ] ?? '#ffffff'

      animateHoverPolygon({
        source,
        sourceLayer,
        featureId,
        previousBorder: 1.678,
        nextBorder: 4,
        bgColor,
        bgHoverColor:
          heatMapColors[heatMapColor].hover,
        duration: 300,
        map,
      })

      map?.setFeatureState(
        {
          source,
          sourceLayer,
          id: featureId,
        },
        {
          hover: true,
          hover_color:
            heatMapColors[heatMapColor][
              codeLevel as keyof (typeof heatMapColors)[EHeatMapCategory]
            ],
          hover_width: 1.678,
        }
      )
    })

    map?.on('mouseleave', layerName, () => {
      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed')
      ) {
        return
      }

      pointsSvgContainerRef?.current?.style('cursor', 'grab')
      map.getCanvas().style.cursor = 'grab'

      map?.setFeatureState(
        {
          source,
          sourceLayer,
          id: featureId,
        },
        { hover: false }
      )

      featureId = undefined
    })
  }

  const createClickEvent = (params: { layerName: string }) => {
    const { layerName } = params

    map?.on('click', layerName, (event) => {
      if (
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed') ||
        hoveredLayerRef?.current?.layerId
      ) {
        return
      }

      const { block } = tileZoomConfiguration

      map?.flyTo({
        center: event.lngLat,
        essential: true,
        speed: 1.4,
        curve: 1.3,
        zoom: block.maxZoom + 0.1,
      })
    })
  }

  const removeBlockLevel = (): void => {
    if (!map) {
      return
    }

    for (const {
      tiles: {
        blocks: { id },
      },
    } of polygonTiles) {
      const find = map?.getSource(`${id}-block-source`)

      if (!find) {
        continue
      }

      const layerName = `${id}-block-polygon`
      const source = `${id}-block-source`

      map.removeLayer(layerName)
      map.removeLayer(`${id}-block-border`)
      map.removeSource(source)
    }
  }

  const changeBlockLevelHeatMapColor = (params: {
    category: EHeatMapCategory
    previousCategory: EHeatMapCategory | null
  }): void => {
    if (!map) {
      return
    }

    const { category, previousCategory } = params

    for (const { id } of blocksTiles) {
      const find = map?.getSource(`${id}-block-source`)

      if (!find) {
        continue
      }

      animateChangeHeatMapByCategories({
        layerName: `${id}-block-polygon`,
        previousCategory: previousCategory || category,
        nextCategory: category,
        duration: 300,
        map,
      })

      map.setPaintProperty(`${id}-block-border`, 'line-color', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        heatMapColors[category].hover,
        '#0368FE',
      ])
    }
  }

  const filterBlockPolygonsByParent = (): void => {
    if (!map || !isLoadedMap || !polygonTiles?.length || !isEnabledHeatMap) {
      return
    }

    const { state, municipality, neighborhood } = polygonNavigation as {
      state: string, municipality: string, neighborhood: string
    }

    if (!state && !municipality && !neighborhood) {
      resetFilterBlockPolygons()
      return
    }

    const properties = neighborhood || municipality || state

    for (const {
      tiles: {
        blocks: { id: blockId },
      },
    } of polygonTiles) {
      if (!blockId) {
        continue
      }

      filterPolygonByUrlCode({
        map,
        layers: [`${blockId}-block-polygon`, `${blockId}-block-border`],
        urlCode: (properties as { fullUrlCode?: string })?.fullUrlCode ?? '',
      })
    }
  }

  const resetFilterBlockPolygons = (): void => {
    if (!map || !polygonTiles?.length || !isLoadedMap || !isEnabledHeatMap) {
      return
    }

    for (const { id } of blocksTiles) {
      const mapLayer = map.getLayer(`${id}-block-polygon`)
      if (!id || !mapLayer) {
        continue
      }
      map.setFilter(`${id}-block-polygon`, null)
      map.setFilter(`${id}-block-border`, null)
    }
  }

  useEffect(() => {
    if (map && isLoadedMap && polygonTiles.length) {
      if (isEnabledHeatMap) {
        loadBlockLevel({ category: heatMapCategory })
      } else {
        removeBlockLevel()
      }
    }
  }, [map, isLoadedMap, polygonTiles, isEnabledHeatMap])

  useEffect(() => {
    if (heatMapCategory && isEnabledHeatMap && map) {
      changeBlockLevelHeatMapColor({
        category: heatMapCategory,
        previousCategory: heatMapCategoryPrevious,
      })
    }
  }, [heatMapCategory])
  type TUbications = {state: string, municipality: string, neighborhood: string}
  useEffect(filterBlockPolygonsByParent, [
    (polygonNavigation as TUbications)?.state,
    (polygonNavigation as TUbications)?.municipality,
    (polygonNavigation as TUbications)?.neighborhood,
    map,
    isLoadedMap,
    polygonTiles,
    isEnabledHeatMap,
  ])

  useEffect(() => {
    if (!map || !isLoadedMap || !polygonTiles?.length) {
      return
    }

    if (
      !mapZoomPrevious ||
      mapZoomPrevious < mapZoom ||
      mapZoom > tileZoomConfiguration.block.minZoom ||
      tileZoomConfiguration.block.minZoom - mapZoom < 0.5 ||
      isFlyTo
    ) {
      return
    }

    setPolygonNavigation({
      level: ESearchLevel.NEIGHBORHOOD,
      properties: undefined,
    })
    setPolygonNavigation({
      level: ESearchLevel.BlOCK,
      properties: undefined,
    })
  }, [mapZoom])

  return (
    <>
      {blocksTiles.map((state) => (
        <PolygonPopup
          key={state.id}
          layer={`${state.id}-block-polygon`}
          level="block"
        />
      ))}
    </>
  )
}

export default BlockPolygonManager
